////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import { encounterField } from './array-types';
// env-types.ts — front-end catalogs for the encounter's environmental "fixed type" classes.
// ----------------------------------------------------------------------------------------------------
// JS/TS mirror of includes/additions.php → final class LightType / final class WeatherType
// (the static ::LIST taxonomies). These are NOT catalog (`items` table) rows — they are hard-coded
// type lists, so the GM "change light type" / "change weather" forms can be rendered ENTIRELY on the
// client with no fetch_items() round-trip. UUIDs/names/variable_names MUST stay byte-identical to the
// PHP LIST entries; update both sides together if the taxonomy ever changes.
//
// template.js already carries the lighter `LIGHT_TYPES` (variable→uuid map) and `WEATHER_TYPES`
// ({id,name,variableName}) globals for other consumers; this module is the turn-widget bundle's own
// typed copy WITH display names for both, mirroring the pre-existing additions.php ↔ template.js dup.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export interface EnvTypeRow {
    item_id: string;
    item_name: string;
    variable_name: string;
}

// Mirror of LightType::LIST (additions.php). Order preserved.
export const LIGHT_TYPE_LIST: ReadonlyArray<EnvTypeRow> = [
    { item_id: '0CC8A592-CF4C-7BE4-DD0C-17A41D690A05', item_name: 'Bright Light',      variable_name: 'bright_light' },
    { item_id: '61D1498F-9175-6906-4B26-8356C28B28C1', item_name: 'Dim Light',         variable_name: 'dim_light' },
    { item_id: '99D3E371-AC3F-7839-28E7-770FE50D24C1', item_name: 'Pitch Black',       variable_name: 'pitch_black' },
    { item_id: '67F42B5B-E3D1-0818-0197-1F36B20FC630', item_name: 'Magical Darkness',  variable_name: 'magical_darkness' },
];

// Mirror of WeatherType::LIST (additions.php). Order preserved (alphabetical, as in PHP).
// Weather axes segmentation (2026-08-14, docs/WEATHER_AXES_PLAN.md): phenomenon axis only now —
// temperature/wind live in TEMPERATURE_BAND_LIST / WIND_LEVEL_LIST below. Renames kept UUIDs:
// Calm→Clear, Snowing And Raining→Sleet, Cloudy Lightning→Thunderstorm.
export const WEATHER_TYPE_LIST: ReadonlyArray<EnvTypeRow> = [
    { item_id: '72D05502-08AA-159C-AA98-D6548840F964', item_name: 'Ashfall',             variable_name: 'ashfall' },
    { item_id: '95D01E12-0C33-928F-4DE8-570AD806D10B', item_name: 'Clear',               variable_name: 'clear' },
    { item_id: '242858C1-C334-3A4C-7C5C-9706495750DD', item_name: 'Cloudy',              variable_name: 'cloudy' },
    { item_id: '1A485B48-C8F4-DF38-BB69-4FE828FDC8A5', item_name: 'Dust Storm',          variable_name: 'dust_storm' },
    { item_id: '6B3F3EA7-DF1A-7F16-38D2-12542984DBD7', item_name: 'Hail',                variable_name: 'hail' },
    { item_id: '351702BB-5901-87AF-85EC-C8A6FBB7ECF9', item_name: 'Hazy',                variable_name: 'hazy' },
    { item_id: 'AFF257FE-DA6E-02E3-86FA-E06FF598180A', item_name: 'Heavy Rain',          variable_name: 'heavy_rain' },
    { item_id: 'BE024638-C2F8-8F7B-2C08-BF017BBFB56F', item_name: 'Heavy Snow',          variable_name: 'heavy_snow' },
    { item_id: '7DF71D1B-2794-00E9-C672-3A95E279E521', item_name: 'Partly Cloudy',       variable_name: 'partly_cloudy' },
    { item_id: '5DF3C480-7754-4872-302E-63CC3C4A9F03', item_name: 'Raining',             variable_name: 'raining' },
    { item_id: 'C4097260-89C0-CB3C-19DD-B76A305EC039', item_name: 'Sleet',               variable_name: 'sleet' },
    { item_id: '5ADA1900-D1AD-198D-A299-EB7EC01ADCBD', item_name: 'Snowing',             variable_name: 'snowing' },
    { item_id: '828845FB-4210-2C7A-23CE-4223EDAD52D5', item_name: 'Thunderstorm',        variable_name: 'thunderstorm' },
];

// Mirror of TemperatureBand::LIST (additions.php), whose item_type is `tempurature_type`.
// Semantic order (coldest → hottest).
export const TEMPERATURE_BAND_LIST: ReadonlyArray<EnvTypeRow> = [
    { item_id: '407B4AC5-27C4-60AD-FD56-9189FCDC8AE6', item_name: 'Frigid',    variable_name: 'freezing' },
    { item_id: 'EE3151A4-5BA0-52EB-85C8-B4E829E888BF', item_name: 'Chill',     variable_name: 'cold' },
    { item_id: '126B1B2D-5ED1-1DA4-AE38-34301FAEF2C2', item_name: 'Cool',      variable_name: 'cool' },
    { item_id: '4C543268-FEF1-23FE-4AFF-5A01A4F81953', item_name: 'Temperate', variable_name: 'temperate' },
    { item_id: '02673CAF-5E3B-966B-24EF-A6C8883169D8', item_name: 'Warm',      variable_name: 'warm' },
    { item_id: '41F03902-005B-8110-850D-B95DC7A01CB3', item_name: 'Hot',       variable_name: 'hot' },
    { item_id: '96DC27AA-0BBA-D76C-BA6E-C9DDAD103069', item_name: 'Scorching', variable_name: 'scorching' },
];

// Mirror of WindLevel::LIST (additions.php). Semantic order (calm → violent). Direction is the
// separate scalar encounter_wind_direction (integer degrees 0-359, blows-TOWARD, 0°=map north).
export const WIND_LEVEL_LIST: ReadonlyArray<EnvTypeRow> = [
    { item_id: '6BB72BB2-74A1-B2F6-D856-D4A7561C87A6', item_name: 'Still',       variable_name: 'still' },
    { item_id: 'EC7ACDFA-A5FE-77B7-3451-C5C080DD71B5', item_name: 'Breeze',      variable_name: 'breeze' },
    { item_id: '9283858C-98CD-FA04-72FC-5D2B9166A9D8', item_name: 'Strong Wind', variable_name: 'windy' },
    { item_id: '29D3FFBA-3DB6-15C1-F88C-B72CFADAE907', item_name: 'Gale',        variable_name: 'gale' },
    { item_id: 'EBC4D926-EF60-DD81-248E-54F6F31F29E0', item_name: 'Storm Wind',  variable_name: 'storm' },
];

// UUID → display name (case-insensitive on the UUID, matching how the DB stores upper-case GUIDs).
// Returns '' when the id is unknown so callers can fall back to a server-resolved name.
function _envNameOf(list: ReadonlyArray<EnvTypeRow>, uuid: string | null | undefined): string {
    if (!uuid) return '';
    const want = String(uuid).toUpperCase();
    const row = list.find(r => r.item_id.toUpperCase() === want);
    return row ? row.item_name : '';
}

export function lightTypeName(uuid: string | null | undefined): string {
    return _envNameOf(LIGHT_TYPE_LIST, uuid);
}

export function weatherTypeName(uuid: string | null | undefined): string {
    return _envNameOf(WEATHER_TYPE_LIST, uuid);
}

export function temperatureBandName(uuid: string | null | undefined): string {
    return _envNameOf(TEMPERATURE_BAND_LIST, uuid);
}

export function windLevelName(uuid: string | null | undefined): string {
    return _envNameOf(WIND_LEVEL_LIST, uuid);
}

// UUID → CSS base-light class for the .xz-viewport (e.g. 'dim-light'). The viewport's lighting
// classes are EXACTLY the catalog variable_name with underscores→hyphens
// ('bright_light'→'bright-light', 'dim_light'→'dim-light', 'pitch_black'→'pitch-black',
// 'magical_darkness'→'magical-darkness'), so derive the class from LIGHT_TYPE_LIST instead of
// duplicating a hard-coded UUID→class map (this replaces the three copies that lived in
// the retired 2-D map widget/XZVisibility). Returns null when the id is unknown/empty — callers keep the
// currently-rendered light in that case rather than silently slamming the map to pitch-black.
export function lightClassForId(uuid: string | null | undefined): string | null {
    if (!uuid) return null;
    const want = String(uuid).toUpperCase();
    const row = LIGHT_TYPE_LIST.find(r => r.item_id.toUpperCase() === want);
    return row ? row.variable_name.replace(/_/g, '-') : null;
}

function _campaignCatalogId(campaignArr: unknown, key: string): string | null {
    if (!campaignArr || typeof campaignArr !== 'object') return null;
    const arr = campaignArr as { campaigns?: Record<string, unknown>; [key: string]: unknown };
    const bucket = arr.campaigns;
    let value: unknown = bucket && Object.prototype.hasOwnProperty.call(bucket, key)
        ? bucket[key]
        : arr[key];
    while (Array.isArray(value)) value = value[0];
    if (value && typeof value === 'object') {
        const items = (value as { items?: { item_id?: unknown } }).items;
        if (items && typeof items.item_id === 'string') value = items.item_id;
    }
    return typeof value === 'string' && value !== '' ? value : null;
}

// Nullable encounter_light is a GM override. A missing value delegates to the campaign light Janus
// derives from the clock; callers pass the current campaign cache entry so 2D rendering and vision
// use exactly the same effective base as PHP's ft_effective_encounter_light().
export function effectiveEncounterLightId(encounterArr: unknown, campaignArr: unknown): string | null {
    const override = encounterField<string>(encounterArr, 'encounter_light');
    if (lightClassForId(override)) return override!;
    const automatic = _campaignCatalogId(campaignArr, 'campaign_light');
    return lightClassForId(automatic) ? automatic : null;
}

// UUID → weather variable_name for the `.weather-layer` data-weather attr (e.g. 'heavy_rain').
// Catalog-derived mirror of lightClassForId. Returns null for an unknown/empty id so callers can
// KEEP the current weather instead of silently resetting to 'calm' — the global
// getWeatherVariableName() conflates unknown → 'calm', which can't express "don't change it".
export function weatherVariableForId(uuid: string | null | undefined): string | null {
    if (!uuid) return null;
    const want = String(uuid).toUpperCase();
    const row = WEATHER_TYPE_LIST.find(r => r.item_id.toUpperCase() === want);
    return row ? row.variable_name : null;
}
